import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { getLoginLink } from "@danishagro/shared/src/helpers/getLoginLink.helper";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useStickyBannerMessages } from "@danishagro/shared/src/hooks/useStickyBannerMessages.hook";
import { B2B_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";
import { DA_Badge } from "@danishagro/shared/src/components/organisms/SiteHeader/Badge/Badge.component";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { getRelativeRootOnLocalhost } from "@danishagro/shared/src/helpers/getRelativeUrlOnLocalhost.helper";
import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";
import { useAccountButton } from "./hooks/useAccountButton";
import { useCartButton } from "./hooks/useCartButton";
import { useSearchButton } from "./hooks/useSearchButton";
import S from "./ToolbarB2B.module.scss";

type DA_ToolbarB2BProps = {
    isLoginOptionsTrue?: boolean;
};

export const DA_ToolbarB2B = ({ isLoginOptionsTrue }: DA_ToolbarB2BProps) => {
    const { isMobileOrTablet } = useScreen();
    const { currentSite, displayFavoritesList, siteVariant } = useAppData();
    const { getDictionaryString } = useTranslations();
    const { presentBottomSheet, dismissBottomSheet } = useBottomSheet();
    const messages = Array.isArray(globalThis?.announcementMessages)
        ? globalThis?.announcementMessages
        : null;
    const activeStickyBanners = useStickyBannerMessages(messages);
    const [stickyBannersToShow, setStickyBannersToShow] = useState([]);
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

    const isDvEnergy = siteVariant.alias === "dv-energy";

    // ExcellenceProgramme CMS
    const isExcellenceProgrammeCmsSite =
        typeof globalThis?.isUsingCustomAd === "string"
            ? globalThis.isUsingCustomAd.toLowerCase() === "true"
            : false;

    // FavoritesListUrl
    const favoritesListUrl = `${getRelativeRootOnLocalhost(B2B_SITE_ORIGIN)}/favorites`;

    // Cart
    const { orderLines, isLoadingBasket } = useBasket();
    const { showCart, onCartButtonClicked, isCartMenuActive } = useCartButton();
    const isCartMenuActiveClass = isCartMenuActive ? S.isActive : undefined;

    // Account
    const { onAccountButtonClicked, accountBadge, isAccountMenuActive } = useAccountButton();
    const isAccountMenuActiveClass = isAccountMenuActive ? S.isActive : undefined;

    // Search
    const { showSearch, onSearchButtonClicked, isSearchMenuActive } = useSearchButton();
    const isSearchMenuActiveClass = isSearchMenuActive ? S.isActive : undefined;

    // Message
    // Filter out banners already shown in the session
    const SHOW_STICKY_BANNER = "shownStickyBanners";
    const BOTTOM_SHEET_NAME = "sticky-banner";
    const SNAP_POINT_BOTTOM_SHEET = 200;

    useEffect(() => {
        const shownBanners = sessionStorage.getItem(SHOW_STICKY_BANNER)
            ? JSON.parse(sessionStorage.getItem(SHOW_STICKY_BANNER))
            : [];
        const bannersToShow = activeStickyBanners?.filter(
            (banner) => !shownBanners.includes(banner.id)
        );
        setStickyBannersToShow(bannersToShow);
    }, [activeStickyBanners]);

    // Common function to handle banner dismissal
    const dismissBanner = useCallback((bannerId) => {
        // Update session storage to mark this banner as shown
        const shownBanners = JSON.parse(sessionStorage.getItem(SHOW_STICKY_BANNER) || "[]");
        if (!shownBanners.includes(bannerId)) {
            shownBanners.push(bannerId);
            sessionStorage.setItem(SHOW_STICKY_BANNER, JSON.stringify(shownBanners));
        }

        // Remove this banner from the stickyBannersToShow
        setStickyBannersToShow((prev) => prev.filter((b) => b.id !== bannerId));
    }, []);

    const showStickyBannerMessage = useCallback(
        (banner) => {
            setIsBottomSheetOpen(true);
            // Present the bottom sheet with the banner content
            presentBottomSheet(
                <div className={S.bottomSheetContent}>
                    <span
                        className={S.bottomSheetText}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(banner.content),
                        }}
                    />
                    <DA_Button
                        className={S.bottomSheetCloseBtn}
                        title={getDictionaryString("OK")}
                        onClick={() => {
                            dismissBottomSheet();
                            setIsBottomSheetOpen(false);
                        }}
                    />
                </div>,
                {
                    id: `${BOTTOM_SHEET_NAME}-${banner.id}`,
                    contentPadding: true,
                    headerTitle: <>{banner.headline}</>,
                    snapPoints: [SNAP_POINT_BOTTOM_SHEET],
                }
            );
            dismissBanner(banner.id);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dismissBottomSheet, presentBottomSheet, dismissBanner]
    );

    const handleCloseBanner = useCallback(
        (event, bannerId) => {
            event.stopPropagation();
            dismissBanner(bannerId);
        },
        [dismissBanner]
    );

    const totalQuantity = useMemo(() => {
        if (orderLines?.[0]) {
            return orderLines.reduce((total, line) => total + line.quantity, 0);
        }

        return 0;
    }, [orderLines]);

    return (
        <Fragment>
            {isMobileOrTablet ? (
                <div className={cn(S.toolbar, currentSite === "B2C" && S.toolbarB2c)}>
                    {isMobileOrTablet &&
                        !isBottomSheetOpen &&
                        stickyBannersToShow?.map((banner) => (
                            <div
                                className={S.messageBottom}
                                key={banner.id}
                                onClick={() => showStickyBannerMessage(banner)}
                                onKeyPress={(e) =>
                                    e.key === "Enter" && showStickyBannerMessage(banner)
                                }
                                role="button"
                                tabIndex={0}
                            >
                                <button
                                    className={S.closeMessageBtn}
                                    onClick={(e) => handleCloseBanner(e, banner.id)}
                                    aria-label={getDictionaryString("close")}
                                >
                                    <DA_Icon className={S.closeIcon} name={DA_IconNames.Close} />
                                </button>
                                <div className={S.messageBottomBox}>
                                    <span className={S.messageTitle}>{banner.headline}</span>
                                    <span
                                        className={S.messageContent}
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(banner.content),
                                        }}
                                    />
                                </div>
                            </div>
                        ))}

                    <div className={cn(S.inner, currentSite === "B2C" && S.innerB2c)}>
                        {isExcellenceProgrammeCmsSite ? (
                            <DA_BasicLink
                                href="/logout"
                                target="_self"
                                conventionalRouting
                                className={S.buttonLink}
                            >
                                <DA_Icon name={DA_IconNames.SignOut} className={S.buttonIcon} />
                            </DA_BasicLink>
                        ) : (
                            <>
                                {/** Cart */}
                                {!isDvEnergy && isLoginOptionsTrue && showCart ? (
                                    <button
                                        onClick={onCartButtonClicked}
                                        className={cn(S.button, isCartMenuActiveClass)}
                                        aria-label={getDictionaryString("accessibilityButtonCart")}
                                    >
                                        <DA_Icon
                                            name={DA_IconNames.CartWeighted}
                                            className={S.icon}
                                        />

                                        {/** Quantity Badge */}
                                        <DA_Badge
                                            size="small"
                                            text={totalQuantity > 99 ? "99+" : totalQuantity}
                                            className={cn(S.badge)}
                                            showSpinner={isLoadingBasket}
                                        />

                                        <span>{getDictionaryString("header.cart")}</span>
                                    </button>
                                ) : null}

                                {/** B2B */}
                                {displayFavoritesList && (
                                    <DA_BasicLink
                                        href={favoritesListUrl}
                                        target="_self"
                                        className={cn(
                                            S.serviceButton,
                                            S.button,
                                            currentSite === "B2C" && S.serviceButtonB2c
                                        )}
                                        ariaLabel={getDictionaryString(
                                            "accessibilityFavotiesLinkText"
                                        )}
                                    >
                                        <DA_Icon
                                            name={DA_IconNames.HeartOutline}
                                            className={S.icon}
                                        />
                                        <span>{getDictionaryString("header.favorites")}</span>
                                    </DA_BasicLink>
                                )}

                                {/** Account: Logged Out */}
                                {isLoginOptionsTrue && !globalThis.isAuthenticated ? (
                                    <DA_BasicLink
                                        href={getLoginLink(currentSite)}
                                        target="_self"
                                        className={cn(
                                            S.button,
                                            currentSite === "B2C" && S.serviceButtonB2c
                                        )}
                                        ariaLabel={getDictionaryString("accessibilityMobileLogin")}
                                    >
                                        <DA_Icon
                                            name={DA_IconNames.UserLoggedOutWeighted}
                                            className={S.icon}
                                        />

                                        <span>{getDictionaryString("toolbarLabelProfile")}</span>
                                    </DA_BasicLink>
                                ) : null}

                                {/** Account: Logged In */}
                                {isLoginOptionsTrue && globalThis.isAuthenticated ? (
                                    <button
                                        onClick={onAccountButtonClicked}
                                        className={cn(S.button, isAccountMenuActiveClass)}
                                        aria-label={getDictionaryString("accountPicker")}
                                    >
                                        <DA_Icon
                                            name={DA_IconNames.UserLoggedInWeighted}
                                            className={S.icon}
                                        />

                                        {/** Notifications Badge */}
                                        {accountBadge > 0 ? (
                                            <DA_Badge text={accountBadge} className={S.badge} />
                                        ) : null}

                                        <span>{getDictionaryString("header.profile")}</span>
                                    </button>
                                ) : null}
                            </>
                        )}

                        {/** Search */}
                        {!isDvEnergy && showSearch ? (
                            <button
                                onClick={onSearchButtonClicked}
                                className={cn(
                                    S.button,
                                    isSearchMenuActiveClass,
                                    !isLoginOptionsTrue && S.searchFullWidthBtn
                                )}
                                aria-label={getDictionaryString("accessibilityButtonSearch")}
                            >
                                <DA_Icon name={DA_IconNames.ZoomWeighted} className={S.icon} />
                                <span>{getDictionaryString("header.search")}</span>
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </Fragment>
    );
};
